/* =========================================================
   V_B2D_PDP_67 | "What's Inside This Set" — Box Set Contents Above ATC

   Which arm sees the module is decided by CSS from the body class set in
   c-intelligems-tests.js; this file never changes what is visible. It does two
   things for the module once it is on screen:

   1. data-view-event (block setting) is pushed to Intelligems once, when the
      module scrolls into the viewport. For Control the block is display: none,
      so it never intersects and the event never fires.

   2. Cover lookup, for books that have no image (titles read from the product
      description). Only when data-cover-search is present ("Look up covers" on),
      and only from the store's own single-book products, via the predictive
      search URL Liquid renders: the title must match, the product must not be
      a set, it must be the same format as the set (no hardback cover on a
      paperback set) and not a special edition the set isn't, and when the
      set's title names an author, that surname must appear in the product;
      a single naming the author in its own title is preferred.
      A book with no confident match keeps its title placeholder rather than
      risk the wrong cover. Only tiles visible on the current screen are looked
      up (the rest sit behind the "+N more" tile). Results are cached per
      browser in localStorage; failed requests are not, and at most three books
      are looked up at once. With data-cover-all="true" covers are applied only
      if every book was found.
   ========================================================= */

(function () {
  var CACHE_KEY = 'c-pdp67-covers-v6';
  var SET_PATTERN = /\b(collection|box set|books? set|\d+\s*(?:books|novels))\b/i;
  // Editions whose cover differs from the standard one in a set.
  // Any "… Edition" (Illustrated, MinaLima, Hufflepuff, Deluxe …) or a bracketed
  // language ("(Latin)") is a different cover from the one in a standard set.
  var EDITION_PATTERN = /\b(edition|illustrated|deluxe|collector'?s|anniversary|sprayed edges?|signed|clothbound|minalima|large print|slipcase|latin|welsh|irish)\b/i;
  var FORMATS = ['paperback', 'hardback', 'board book'];

  function track(name) {
    if (!name) return;
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event: name });
  }

  // ---------- matching helpers ----------

  // Lookup form of a listed title: no trailing "(Book 4)" / "(Novellas 1.5)",
  // ": Illustrated By …", ellipsis or full stop.
  function clean(title) {
    return String(title || '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .replace(/\s*:\s*illustrated by\b.*$/i, '')
      .replace(/(\.\.\.|…)\s*$/, '')
      .replace(/\.\s*$/, '')
      .trim();
  }

  function norm(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[’‘]/g, "'")
      .replace(/[^a-z0-9& ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // "… by Robert Jordan: Books 1-5 …" / "Keri Lake Collection: …"
  var NOT_AUTHOR = /\b(the|of|and|series|epic|works|stories|story|books?|christmas|storybook|picture|fairy|tales?|classics?|complete|collection|ultimate|first|reading|library|mini)\b/i;
  function authorOf(setTitle) {
    var stop = '(?:\\s*(?::|\\||\\(|\\s[-–]\\s|\\s\\d+\\s*(?:books?|novels?|mini|picture)\\b|\\s(?:collection|box set|series|novels|books|book|complete|story|stories|illustrated)\\b|$))';
    var by = new RegExp('\\bby\\s+(.+?)' + stop, 'i').exec(setTitle);
    if (by && !NOT_AUTHOR.test(by[1])) return by[1].trim();
    // "Kristin Hannah 5 Books Collection" / "Julia Donaldson Story Collection"
    var lead = /^((?:[A-Z][\w.'-]*\s){1,3}?[A-Z][\w.'-]*)\s+(?:(?:story|classic)\s+)?(?:collection|novels|\d+\s+(?:books?|novels?))\b/i.exec(setTitle);
    if (lead && !NOT_AUTHOR.test(lead[1])) return lead[1].trim();
    return '';
  }

  function surname(author) {
    var words = author.split(/\s+/).filter(function (w) { return w.length > 1; });
    return words.length ? norm(words[words.length - 1]) : '';
  }

  // A leading "The / A / An" is optional: a single may be listed as "Risk" for "The Risk".
  function bare(text) {
    return text.replace(/^(?:the|a|an) /, '');
  }

  function titleMatches(candidate, wanted) {
    var c = bare(norm(candidate));
    var w = bare(wanted);
    return c === w || c.indexOf(w + ' ') === 0;
  }

  function withWidth(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // An error response (a 429 from a throttled source, a 5xx) rejects, so it is
  // treated as "try again next visit" rather than cached as "no cover".
  function getJSON(url) {
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  // Runs task(item) for every item, at most `limit` at a time.
  function pool(items, limit, task) {
    var results = new Array(items.length);
    var next = 0;
    function worker() {
      if (next >= items.length) return Promise.resolve();
      var i = next++;
      return task(items[i]).then(function (value) {
        results[i] = value;
        return worker();
      });
    }
    var workers = [];
    for (var w = 0; w < Math.min(limit, items.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () { return results; });
  }

  // ---------- sources ----------

  // The set's own format ("… - Paperback") and edition markers, so a book is
  // only matched to a single of the same kind.
  function formatOf(text) {
    var t = String(text || '').toLowerCase();
    for (var f = 0; f < FORMATS.length; f++) if (t.indexOf(FORMATS[f]) !== -1) return FORMATS[f];
    return '';
  }

  function sameKind(productTitle, setTitle) {
    if (EDITION_PATTERN.test(productTitle) && !EDITION_PATTERN.test(setTitle)) return false;
    var setFormat = formatOf(setTitle);
    var productFormat = formatOf(productTitle);
    return !setFormat || !productFormat || setFormat === productFormat;
  }

  function fromStore(searchUrl, title, author, last, setTitle) {
    var wanted = norm(title);
    var queries = author ? [title + ' ' + author, title] : [title];

    function attempt(i) {
      if (i >= queries.length) return Promise.resolve('');
      var url = searchUrl + '.json?q=' + encodeURIComponent(queries[i]) +
        '&resources[type]=product&resources[limit]=10&resources[options][fields]=title,vendor,body';
      return getJSON(url).then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        // Of the matches, one that names the author in its own title ("… by
        // J. K. Rowling") is preferred: those are the standard singles, while
        // bare titles are often an illustrated or special printing.
        var fallback = '';
        for (var p = 0; p < products.length; p++) {
          var product = products[p];
          if (!product.image || SET_PATTERN.test(product.title)) continue;
          if (!titleMatches(product.title, wanted)) continue;
          if (!sameKind(product.title, setTitle)) continue;
          if (last && norm(product.title + ' ' + (product.body || '') + ' ' + (product.tags || []).join(' ')).indexOf(last) === -1) continue;
          if (!last || norm(product.title).indexOf(last) !== -1) return withWidth(product.image, 320);
          if (!fallback) fallback = withWidth(product.image, 320);
        }
        return fallback || attempt(i + 1);
      });
    }

    return attempt(0);
  }

  // ---------- cache ----------

  function readCache() {
    try { return JSON.parse(window.localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; }
  }

  function writeCache(cache) {
    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* storage unavailable */ }
  }

  // ---------- lookup ----------

  function lookupCovers(root) {
    var searchUrl = root.getAttribute('data-cover-search');
    if (!searchUrl || !window.fetch) return;

    // Tiles behind the "+N more" tile are display: none and not looked up.
    var books = Array.prototype.filter.call(root.querySelectorAll('[data-c-pdp67-title]'), function (book) {
      return book.getClientRects().length > 0;
    });
    if (!books.length) return;

    var setTitle = root.getAttribute('data-set-title') || '';
    var author = authorOf(setTitle);
    var last = surname(author);
    var requireAll = root.getAttribute('data-cover-all') === 'true';
    var cache = readCache();

    // Three books in flight at a time: a 30-book set must not fire dozens of
    // searches at once (that is also what gets a visitor throttled). A failed
    // request is not cached, so a source that was down is retried next visit.
    pool(books, 3, function (book) {
      var title = clean(book.getAttribute('data-c-pdp67-title'));
      var key = norm(title) + '|' + last;
      if (Object.prototype.hasOwnProperty.call(cache, key)) {
        return Promise.resolve({ book: book, title: title, src: cache[key] });
      }
      return fromStore(searchUrl, title, author, last, setTitle)
        .catch(function () { return null; })
        .then(function (src) {
          if (src !== null) cache[key] = src;
          return { book: book, title: title, src: src || '' };
        });
    }).then(function (results) {
      writeCache(cache);
      var found = results.filter(function (r) { return r.src; });
      if (!found.length || (requireAll && found.length !== results.length)) return;

      found.forEach(function (r) {
        var cover = r.book.querySelector('.c-pdp67__cover');
        if (!cover) return;
        var img = new Image();
        img.onload = function () {
          img.className = 'c-pdp67__found';
          cover.innerHTML = '';
          cover.appendChild(img);
          cover.classList.remove('c-pdp67__cover--placeholder');
          r.book.removeAttribute('data-c-pdp67-title');
        };
        img.alt = r.title;
        img.width = 160;
        img.height = 240;
        img.src = r.src;
      });
    });
  }

  // ---------- init ----------

  function init(root) {
    if (root.hasAttribute('data-c-pdp67-ready')) return;
    root.setAttribute('data-c-pdp67-ready', '');

    var viewEvent = root.getAttribute('data-view-event');

    if (!('IntersectionObserver' in window)) {
      lookupCovers(root);
      return;
    }

    // Covers start loading a little before the module scrolls in; the view
    // event waits until it is actually in the viewport. Neither can fire for
    // Control, where the module is display: none.
    var nearObserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        nearObserver.disconnect();
        lookupCovers(root);
      }
    }, { rootMargin: '400px 0px' });
    nearObserver.observe(root);

    if (viewEvent) {
      var viewObserver = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          viewObserver.disconnect();
          track(viewEvent);
        }
      });
      viewObserver.observe(root);
    }
  }

  function start() {
    var roots = document.querySelectorAll('[data-c-pdp67]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: the section is re-rendered in place when settings change.
  document.addEventListener('shopify:section:load', start);
})();
